// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {emitTrackEvent, hasSlot} from 'common/util/dom';

import {TrackingEvent} from './tracking';
import {template} from './template';
import css from './index.inline.styl';

export enum CollapsibleTheme {
  DEFAULT = `default`,
  INLINE = `inline`,
  RED = `red`,
}

const COMPONENT_NAME = `mp-section`;

type SectionJsxProps = {
  on?: {
    track?: (ev: CustomEvent) => void;
    change?: (ev: CustomEvent<{state: `open` | `closed`}>) => void;
  };
  attrs?: {
    'enable-row-click'?: boolean;
    'header-title'?: string;
    'header-title-lowercase'?: boolean;
    'is-closed'?: boolean;
    theme?: CollapsibleTheme;
    'hide-caret'?: boolean;
    'tooltip-text'?: string;
    'non-collapsible'?: boolean;
    'is-container'?: boolean;
  };
  children?: React.ReactNode;
};

export class Section extends Component<{isOpen: boolean}, SectionJsxProps[`attrs`]> {
  static get attrsSchema(): AttrsSchema<SectionJsxProps[`attrs`]> {
    return {
      ...super.attrsSchema,
      'enable-row-click': {
        type: `boolean`,
        description: `Enable row click on the entire component`,
      },
      'header-title': `string`,
      'header-title-lowercase': `boolean`,
      'is-closed': `boolean`,
      theme: {
        default: CollapsibleTheme.DEFAULT,
        type: `string`,
        enum: Object.values(CollapsibleTheme),
        description: `The theme of collapsible which determines the styling`,
      },
      'hide-caret': {
        type: `boolean`,
        description: `Hides the collapsible caret by default, but show on hover`,
      },
      'tooltip-text': {
        type: `string`,
        description: `ToolTip to be used when hovering over title`,
      },
      'non-collapsible': {
        type: `boolean`,
        description: `if component is not collapsible, collapsible by default`,
      },
      'is-container': {
        type: `boolean`,
        description: `when set, the section renders the contents in a styled container`,
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,

      defaultState: {
        isOpen: true,
      },

      helpers: {
        hasSlot,
        CollapsibleTheme,
        toggleOpen: () => {
          const isOpen = !this.state.isOpen;
          const state = isOpen ? `open` : `closed`;

          this.update({isOpen});
          this.track(state);
          this.dispatchEvent(new CustomEvent(`change`, {detail: {state}}));
        },
      },
    };
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (attr === `is-closed`) {
      this.update({isOpen: !this.attr(`is-closed`)});
    }
  }

  track(state) {
    const event = state === `open` ? TrackingEvent.OpenedCollapsibleSection : TrackingEvent.ClosedCollapsibleSection;
    emitTrackEvent(this, {
      eventName: event,
      eventPrefix: COMPONENT_NAME,
      props: {
        'Collapsible state': state,
        'Section name': this.attr(`header-title`),
      },
      cancelable: true,
    });
  }
}

export const SectionEl = defineAndMakeJsxFactory<SectionJsxProps>(COMPONENT_NAME, Section);
export const SectionReact = panel2react<SectionJsxProps>(COMPONENT_NAME, Section);
